import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestListObjectType, SupplierRequestType } from '@econtractor/types';
import EOverlay from 'components/others/EOverlay';
import ShareDocument from 'components/ShareLink';
import { UserNameItem } from 'modules/user';
import { escapeHtml } from 'utils/helpers/functions/escapeHtml';
import RequestActions from '../RequestActions';
import RequestObjectChip from '../RequestObjectChip';
import RequestStatusBadge from '../RequestStatusBadge';
import { SUPPLIER_REQUEST_STATUS } from '../model/constants';

const NOTE_OVERLAY_MAX_LENGTH = 80;

interface SupplierRequestsTableRowProps {
    row: SupplierRequestType;
    canOps: boolean;
    onOpen: (requestId: number) => void;
    onClone: (row: SupplierRequestType) => void;
    onChanged: () => void;
    onSendEmail: (row: SupplierRequestType) => void;
    onFilterByObject: (object: SupplierRequestListObjectType) => void;
    showObject: boolean;
    showDate: boolean;
    showUser: boolean;
    showNote: boolean;
}

const SupplierRequestsTableRow = ({
    row,
    canOps,
    onOpen,
    onClone,
    onChanged,
    onSendEmail,
    onFilterByObject,
    showObject,
    showDate,
    showUser,
    showNote,
}: SupplierRequestsTableRowProps) => {
    const { t } = useTranslation(['Vendors']);
    const dt = DT.fromMillis(Number(row.dt || 0));
    const fileUrl = String(row.filePublicUrl || '').trim();
    const note = String(row.note || '').trim();
    const notePreview =
        note.length > NOTE_OVERLAY_MAX_LENGTH
            ? `${note.slice(0, NOTE_OVERLAY_MAX_LENGTH).trimEnd()}…`
            : note;
    const noteHtml = notePreview ? `<div>${escapeHtml(notePreview)}</div>` : '';
    const vendorTitle = row.vendorName || `#${row.vendorId}`;
    const isDeleted = Number(row.status) === SUPPLIER_REQUEST_STATUS.DELETED;

    return (
        <tr>
            <td className="vendor" data-label={t('supplierRequests.colVendor')} title={vendorTitle}>
                <span
                    className="text-break cursor-pointer"
                    onClick={() => onOpen(Number(row.ID))}
                >
                    {vendorTitle}
                </span>
            </td>
            {showObject ? (
                <td className="objects" data-label={t('supplierRequests.colObjects')}>
                    {row.objects?.length ? (
                        <div className="d-flex flex-wrap gap-1">
                            {row.objects.map((object) => (
                                <RequestObjectChip
                                    key={object.objectId}
                                    object={object}
                                    onClick={() => onFilterByObject(object)}
                                />
                            ))}
                        </div>
                    ) : (
                        <span className="text-muted">—</span>
                    )}
                </td>
            ) : null}
            <td className="status" data-label={t('supplierRequests.colStatus')}>
                <RequestStatusBadge status={Number(row.status)} />
            </td>
            {showDate ? (
                <td className="date" data-label={t('supplierRequests.colDate')}>
                    <span className="d-block font-medium">{dt.toFormat('dd.MM.yyyy')}</span>
                    <span className="text-muted">{dt.toFormat('HH:mm')}</span>
                </td>
            ) : null}
            {showUser ? (
                <td className="username" data-label={t('supplierRequests.colUser')}>
                    <UserNameItem
                        smallMode
                        user={{
                            userName: row.userName,
                            userEmail: row.userEmail,
                            userPicture: row.userPicture || undefined,
                        }}
                    />
                </td>
            ) : null}
            {showNote ? (
                <td className="note" data-label={t('supplierRequests.colNote')}>
                    <EOverlay text={noteHtml} />
                </td>
            ) : null}
            <td className="action" data-label={t('supplierRequests.colActions')}>
                <div className="d-inline-flex align-items-center justify-content-end gap-1">
                    <ShareDocument
                        text={t('supplierRequests.shareText')}
                        link={fileUrl}
                        inputMessageDlg
                        disabled={isDeleted || !fileUrl}
                    />
                    <RequestActions
                        row={row}
                        canOps={canOps}
                        onOpen={onOpen}
                        onClone={onClone}
                        onChanged={onChanged}
                        onSendEmail={onSendEmail}
                    />
                </div>
            </td>
        </tr>
    );
};

export default SupplierRequestsTableRow;
